/**
 * Device Registration Service for benipaltoys.com
 * Registration runs beside the store reads, never in front of them: the
 * storefront API answers without a device id.
 */

/**
 * Versioned so a move to another API drops the ids the old one issued: device
 * ids are integers per database, so an old id names another device there, or none.
 */
const DEVICE_ID_KEY = "benipaltoys_device_id_v2";
const RETIRED_DEVICE_ID_KEYS = ["benipaltoys_device_id"];

// Bounds each attempt, so a POST that never answers ends in a retry, not a hang.
const REGISTRATION_TIMEOUT_MS = 5000;

interface DeviceInfo {
  name: string;
  uuid: string;
  type: string;
  platform: string;
  manufacturer?: string;
  model?: string;
  systemVersion?: string;
}

class DeviceService {
  private baseURL: string;
  private appId: string | null;
  private deviceId: string | null = null;
  deviceInfo: DeviceInfo | null = null;
  private registrationAttempted: boolean = false;
  private registrationFailed: boolean = false;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";
    this.appId = import.meta.env.VITE_APP_ID || null;
  }

  /**
   * Check if device registration has failed
   */
  hasRegistrationFailed(): boolean {
    return this.registrationFailed;
  }

  /**
   * Get or generate device UUID from localStorage
   */
  private getDeviceUUID(): string {
    const storageKey = "benipaltoys_device_uuid";
    let uuid = localStorage.getItem(storageKey);

    if (!uuid) {
      // Generate a UUID v4
      uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
      localStorage.setItem(storageKey, uuid);
    }

    return uuid;
  }

  /**
   * Get device information from browser
   */
  private getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    // Detect device type - must match API expected values: "iOS", "Android", or "Web"
    let type: "iOS" | "Android" | "Web" = "Web";
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      type = "iOS";
    } else if (/Android/.test(userAgent)) {
      type = "Android";
    }
    // Default to "Web" for desktop browsers and other devices

    // Detect browser/OS
    let manufacturer = "Unknown";
    let model = "Unknown";
    let systemVersion = "";

    if (/Windows/.test(userAgent)) {
      manufacturer = "Microsoft";
      model = "Windows";
      systemVersion = userAgent.match(/Windows NT ([\d.]+)/)?.[1] || "";
    } else if (/Mac OS X/.test(userAgent)) {
      manufacturer = "Apple";
      model = "macOS";
      systemVersion =
        userAgent.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
    } else if (/Linux/.test(userAgent)) {
      manufacturer = "Linux";
      model = "Linux";
    } else if (/Android/.test(userAgent)) {
      manufacturer = "Android";
      model = userAgent.match(/Android ([\d.]+)/)?.[1] || "";
      systemVersion = userAgent.match(/Android ([\d.]+)/)?.[1] || "";
    } else if (/iPhone|iPad/.test(userAgent)) {
      manufacturer = "Apple";
      model = /iPhone/.test(userAgent) ? "iPhone" : "iPad";
      systemVersion =
        userAgent.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
    }

    return {
      name: `Benipal Toys Web - ${platform}`,
      uuid: this.getDeviceUUID(),
      type,
      platform,
      manufacturer,
      model,
      systemVersion,
    };
  }

  /**
   * Register or update device with the API
   * Returns device ID if successful, throws error if failed
   * In production, will retry instead of using dummy IDs
   */
  async registerDevice(retryCount = 0, maxRetries = 3): Promise<string> {
    if (!this.appId) {
      throw new Error(
        "App ID is required. Set VITE_APP_ID environment variable."
      );
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REGISTRATION_TIMEOUT_MS);
    try {
      const deviceInfo = this.getDeviceInfo();
      this.deviceInfo = deviceInfo;

      const response = await fetch(`${this.baseURL}/devices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-App-ID": this.appId,
          ...(this.deviceId && { device_id: this.deviceId }),
        },
        body: JSON.stringify(deviceInfo),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Device registration failed: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          // If response is not JSON, use status text
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      clearTimeout(timeout);

      if (data.success && data.data) {
        this.deviceId = String(data.data.id);
        this.registrationFailed = false;
        // Store device ID in localStorage for future use
        localStorage.setItem(DEVICE_ID_KEY, this.deviceId);
        console.log("✅ Device registered successfully:", this.deviceId);
        return this.deviceId;
      }

      throw new Error("Device registration failed: Invalid response");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      clearTimeout(timeout);
      // Retry if we haven't exceeded max retries
      if (retryCount < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
        console.warn(
          `⚠️ Device registration failed (attempt ${retryCount + 1}/${
            maxRetries + 1
          }), retrying in ${delay}ms...`,
          error.message
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.registerDevice(retryCount + 1, maxRetries);
      }

      // Mark registration as failed after all retries exhausted
      this.registrationFailed = true;
      console.error("❌ Device registration failed after all retries:", {
        baseURL: this.baseURL,
        appId: this.appId,
        error: error.message,
      });

      // In production, throw error instead of using dummy ID
      throw new Error(
        `Device registration failed after ${maxRetries + 1} attempts: ${
          error.message
        }`
      );
    }
  }

  /**
   * The id from an earlier registration, without registering. Null until one succeeds.
   */
  getKnownDeviceId(): string | null {
    if (this.deviceId) {
      return this.deviceId;
    }
    for (const key of RETIRED_DEVICE_ID_KEYS) {
      localStorage.removeItem(key);
    }
    this.deviceId = localStorage.getItem(DEVICE_ID_KEY);
    return this.deviceId;
  }

  /**
   * Get device ID (register if not already registered)
   */
  async getDeviceId(): Promise<string> {
    const known = this.getKnownDeviceId();
    if (known) {
      return known;
    }

    // Attempt device registration (will retry on failure)
    if (!this.registrationAttempted) {
      this.registrationAttempted = true;
      return await this.registerDevice();
    }

    // If registration was already attempted, try again (for retry logic)
    return await this.registerDevice();
  }
}

// Export singleton instance
export const deviceService = new DeviceService();
export default deviceService;
